
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { mixins } from 'vue-class-component';
import VWrapper from '@/components/shared/VWrapper.vue';
import ComoManagedGiftsManager from '@/components/loyalty/ComoManagedGifts.vue';
import Notification from '@/mixins/Notification';
import { Venue } from '@/interfaces/models/Venue';
import { ComoGift, ComoManagedGift } from '@/interfaces/models/ComoManagedGift';
import { ComoManagedGiftFilter, ComoManagedGiftSavePayload } from '@/stores/comoManagedGifts';

const venue = namespace('venue');
const como = namespace('comoManagedGifts');

@Component({
  components: { VWrapper, ComoManagedGiftsManager },
})
export default class ComoManagedGifts extends mixins(Notification) {
  @venue.State('active') public activeVenue!: Venue;
  @como.State('hydratedGifts') public hydratedGifts!: ComoGift[];
  @como.State('managedGifts') public managedGifts!: ComoManagedGift[];
  @como.State('total') public total!: number;
  @como.State('limit') public limit!: number;
  @como.State('page') public page!: number;
  @como.Action('fetchAvailableGifts') public fetchAvailableGifts!: (venueId: string) => Promise<void>;
  @como.Action('fetchManaged') public fetchManaged!: (payload?: any) => Promise<void>;
  @como.Action('save') public save!: (payload: ComoManagedGiftSavePayload) => Promise<void>;
  @como.Action('delete') public remove!: (id: string) => Promise<void>;

  public async mounted() {
    await Promise.all([this.loadManagedGifts({ page: 1, limit: this.limit }), this.loadAvailableGifts()]);
  }

  @Watch('activeVenue')
  public async onVenueChange() {
    if (this.activeVenue?._id) {
      await this.loadAvailableGifts();
    }
  }

  public async loadAvailableGifts(venueId: string = this.activeVenue?._id) {
    if (!venueId) {
      return;
    }

    this.$startLoading('comoGifts.hydrated');
    try {
      await this.fetchAvailableGifts(venueId);
    } catch (e) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('comoGifts.hydrated');
    }
  }

  public async loadManagedGifts(payload: { page: number; limit: number; filter?: ComoManagedGiftFilter }) {
    this.$startLoading('comoGifts');
    try {
      await this.fetchManaged({ page: payload, filter: payload.filter });
    } catch (e) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('comoGifts');
    }
  }

  public async saveGift(payload: ComoManagedGiftSavePayload) {
    this.$startLoading('comoGifts.save');
    try {
      await this.save(payload);
      await this.loadAvailableGifts();
    } catch (e) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('comoGifts.save');
    }
  }

  public async deleteGift(gift: ComoManagedGift) {
    if (!gift._id) {
      return;
    }

    this.$startLoading('comoGifts');
    try {
      await this.remove(gift._id);
      await this.loadAvailableGifts();
    } catch (e) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('comoGifts');
    }
  }
}
